/**
 * The multiplayer API, as the game sees it.
 *
 * The one idea underneath all of this: two players get the same 52 cards in the
 * same order, because the deck comes from one shuffle rather than two.
 *
 * That shuffle used to be handed over as a seed and dealt on each device, which
 * made the whole deck knowable before the first card was turned. For a ranked
 * round it now stays on the server, which deals one card at a time and decides
 * every rule and every point — so a score is a fact about a game the server
 * refereed rather than a number a browser claimed. A solo round still runs
 * entirely here, on the same engine, and is not recorded against anybody.
 *
 * Every call here can fail, and none of them is allowed to stop a round. The
 * game is single-player first and multiplayer second: if the server is down, the
 * player still gets a game, just an unranked one. That is why every function
 * returns a result rather than throwing, and why `main` never awaits one of
 * these before dealing.
 */

import { type CardId, type FinalScore, type PileTotals, type Resolution } from '@/game/types';
import type { ChestItem, ChestOdds, ChestSource, Rarity } from '@/game/chests';

/** The port the API listens on when it is not told otherwise. */
const DEFAULT_API_PORT = '5274';

/**
 * Where the API lives.
 *
 * `VITE_API_BASE` wins when it is set — that is how the containerised build is
 * pointed at a real deployment. Without it, the API is assumed to be on the same
 * host as the page, on its own port.
 *
 * Deriving it from `location` rather than hardcoding `localhost` is the whole
 * point: the game is played on a phone over the LAN, and on that phone
 * `localhost` is *the phone*. A hardcoded default would work perfectly on the
 * development machine and fail on every device anyone actually tests with.
 */
export function apiBase(): string {
  const configured = import.meta.env.VITE_API_BASE as string | undefined;
  if (configured) return configured.replace(/\/$/, '');

  const location = globalThis.location;
  if (!location) return `http://localhost:${DEFAULT_API_PORT}`;
  return `${location.protocol}//${location.hostname}:${DEFAULT_API_PORT}`;
}

const BASE: string = apiBase();

/** The deal protocol's WebSocket (`GET /api/games/ws`): the API's own origin, over `ws:` or `wss:`. */
export function wsUrl(): string {
  return `${BASE.replace(/^http/, 'ws')}/api/games/ws`;
}

/** How long to wait before deciding the server is not there. */
const TIMEOUT_MS = 6000;

/**
 * How long one attempt at a deal call waits before it is counted lost.
 *
 * A play is the one call a player is standing still for: the board has already animated the prediction and
 * cannot turn over the next card until the server says what it is. 2.5s is past the tail of any honest round
 * trip (the worst measured from India was ~350ms) and short enough that a lost request costs a beat rather
 * than a round. A lost attempt is not a failed play any more: it is resent, at the same index, until it lands
 * (see `@/net/deal`).
 */
export const DEAL_TIMEOUT_MS = 2500;

export interface Opponent {
  readonly name: string;
  readonly score: number;
  /** The in-play level they reached. Cosmetic — it ranks nothing. */
  readonly level?: number;
  readonly createdAt: string;
}

export interface Challenge {
  readonly challengeId: string;
  readonly kind: 'async' | 'lobby';
  /** The score to beat, when somebody has already played this deck. */
  readonly target: Opponent | null;
}

export interface Lobby {
  readonly code: string;
  readonly hostName: string;
  /** Null for a STAKED challenge: its id is not handed out by its public code (take it by the code instead). */
  readonly challengeId: string | null;
  /** True for a staked challenge (a fee or a creator). */
  readonly staked?: boolean;
  readonly results: readonly Opponent[];
}

export type Verdict = 'win' | 'loss' | 'draw' | 'unopposed';

export interface Submission {
  /** False when this player had already posted a score for this deck. */
  readonly submitted: boolean;
  readonly score: number;
  readonly outcome: Verdict;
  readonly opponent: Opponent | null;
  readonly results: readonly Opponent[];
  /**
   * The XP this round earned, itemised.
   *
   * Null when nothing was awarded — a round with no moves in it, or a
   * finalisation that had already been recorded. There is deliberately no field
   * a client can send here: the server computes the award from its own replay,
   * and anything arriving on the request is ignored.
   */
  readonly xpGained?: XpGained | null;
  /**
   * Present only on the result that COMPLETED the challenge.
   *
   * The results screen leads with what the win paid; without this it could
   * only say who beat whom, which is not the thing a player staked to find
   * out.
   */
  readonly settled?: {
    readonly won: boolean;
    readonly payout: number;
    readonly stake: number;
  } | null;
  /**
   * Milestone chests this round earned, unopened — a rank-up's, a win's.
   *
   * They ride on the result rather than being fetched afterwards, so the tray
   * can offer them the moment it appears and the rank-up's CONTINUE has one to
   * open. Rolled and stored server-side; the client only animates them.
   */
  readonly chests?: readonly ChestSummary[];
}

/** Success or a reason, never an exception. */
export type Result<T> =
  | { ok: true; value: T }
  /** `status` is the HTTP status when the server ANSWERED; it is absent for a call that never got an answer. */
  | { ok: false; error: string; status?: number; body?: unknown };

/**
 * Whether a failed call is the server's definite "no", as opposed to a call whose outcome is unknown.
 *
 * A timeout, a dropped connection, a 5xx or a 429 says nothing about what the server did (it may have applied
 * the request and lost the reply); only an answered 4xx says it refused. Anything that has to be careful with
 * money or a round asks this before it gives something up.
 */
export function isDefinite(result: { status?: number }): boolean {
  const s = result.status;
  return s !== undefined && s >= 400 && s < 500 && s !== 408 && s !== 429;
}

async function call<T>(
  path: string,
  init?: RequestInit,
  timeoutMs: number = TIMEOUT_MS,
): Promise<Result<T>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${BASE}${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        // Only when there is actually a body. Announcing JSON and then sending
        // nothing is a 400 from Fastify — "body cannot be empty when
        // content-type is set to application/json" — which is how the undo
        // button, whose request carries no body, silently did nothing.
        ...(init?.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(init?.headers ?? {}),
      },
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const error =
        body && typeof body === 'object' && 'error' in body
          ? String((body as { error: unknown }).error)
          : `request failed (${response.status})`;
      return { ok: false, error, status: response.status, body };
    }
    // A 200 whose body will not parse is not a success. Every route here
    // answers with JSON — the only empty reply in the service is the CORS
    // preflight, which never reaches this — so `null` means a truncated or
    // rewritten response, and handing that back as `value` pushes the failure
    // into whichever caller dereferences it first. A ranked round found this
    // the loud way: `#absorb` read `.view` off it mid-game and took the round
    // down with it.
    if (body === null) return { ok: false, error: 'malformed response' };
    return { ok: true, value: body as T };
  } catch (error) {
    // An abort, a refused connection, a DNS failure and an offline phone all
    // land here, and the caller treats them the same way: play on, unranked.
    return { ok: false, error: error instanceof Error ? error.message : 'network error' };
  } finally {
    clearTimeout(timer);
  }
}

/** Claim a game from the shared async pool. */
export function nextAsyncChallenge(): Promise<Result<Challenge>> {
  // Whose seat it is comes from the session; there is no id to send.
  return call<Challenge>('/api/async/next', WITH_SESSION);
}

/** Open a lobby to share with somebody. */
export function createLobby(name: string): Promise<Result<Omit<Lobby, 'results' | 'hostName'>>> {
  return call(`/api/lobbies`, { method: 'POST', body: JSON.stringify({ name }) });
}

/** Join a lobby by its code. */
export function joinLobby(code: string): Promise<Result<Lobby>> {
  return call<Lobby>(`/api/lobbies/${encodeURIComponent(code)}`);
}

/* --------------------------------------------------------------- sessions -- */

/**
 * What the server is willing to say about a ranked round in progress.
 *
 * Notice what is absent: the deck, and the seed it came from. A ranked round is
 * dealt and refereed on the server precisely so the client cannot see what is
 * coming, so this carries the one face-up card and a count of what is left.
 */
export interface SessionView {
  readonly phase: 'idle' | 'dealing' | 'playing' | 'resolving' | 'over';
  readonly activeCard: CardId | null;
  readonly piles: CardId[][];
  readonly pileTotals: PileTotals[];
  readonly score: number;
  readonly busts: number;
  readonly secondsRemaining: number;
  readonly streakLevel: number;
  readonly canUndo: boolean;
  readonly cardsRemaining: number;
  readonly final: FinalScore | null;
}

export interface SessionOpened {
  readonly sessionId: string;
  readonly token: string;
  readonly kind: 'async' | 'lobby';
  readonly view: SessionView;
  /** Moves the server has applied (`Session.seq`): 0 on a fresh round, the cursor a resumed one syncs from. */
  readonly seq?: number;
}

/** A play or an undo: the new table, and the standings once it is over. */
export interface SessionStep {
  readonly resolution?: Resolution;
  readonly view: SessionView;
  readonly result?: Submission;
}

export interface TopScores {
  readonly today: { name: string; score: number } | null;
  readonly allTime: { name: string; score: number } | null;
}

/**
 * The score to beat, for the start screen.
 *
 * Never worth failing over: the caller shows nothing rather than an error, so a
 * scoreboard being down costs the player a line of text and nothing else.
 */
export function topScores(): Promise<Result<TopScores>> {
  return call<TopScores>('/api/scores/top');
}

/* ------------------------------------------------------------------ *
 * Accounts and the $CHAIN economy
 * ------------------------------------------------------------------ */

/** A rank, as the server reports it. Mirrors `@/game/xp`'s `Rank`. */
export interface RankView {
  readonly index: number;
  readonly name: string;
  readonly tier: number;
}

export interface Account {
  readonly id: string;
  readonly username: string;
  readonly isGuest: boolean;
  /**
   * False for a registered account with no password (it only ever signed in by
   * its id): the sign-up sheet offers to give it one. Absent on the sign-in
   * responses, where it does not apply.
   */
  readonly hasPassword?: boolean;
  readonly balance: number;
  readonly xp: number;
  readonly xpLevel: number;
  readonly rank: RankView;
  readonly prevThreshold: number;
  readonly nextThreshold: number;
}

/** One XP award, itemised, as the results tray narrates it. */
export interface XpGained {
  readonly total: number;
  readonly parts: {
    played: number;
    score: number;
    clears: number;
    levels: number;
    streak: number;
    challenge: number;
    win: number;
  };
  readonly xpBefore: number;
  readonly xpAfter: number;
  readonly levelBefore: number;
  readonly levelAfter: number;
  readonly rankBefore: RankView;
  readonly rankAfter: RankView;
}

/* ------------------------------------------------------------------ *
 * Chests
 * ------------------------------------------------------------------ */

/** A chest before it is opened. The roll inside it is not sent until it is. */
export interface ChestSummary {
  readonly id: string;
  readonly source: ChestSource;
  /** What exactly earned it: for `weekly_top`, `<board>:<ISO week>:<place>` (the arrival's label). */
  readonly sourceRef?: string;
  readonly startRarity: Rarity;
  readonly createdAt: string;
  readonly opened: boolean;
  /** A guest opened it: the roll was shown, the contents wait for an account. */
  readonly held?: boolean;
  /** A guest's chest dies at this instant unless they register first; null for a registered player's. */
  readonly expiresAt?: string | null;
}

/** The whole stored roll, for the chest screen to animate. */
export interface ChestView extends ChestSummary {
  readonly steps: readonly Rarity[];
  readonly finalRarity: Rarity;
  readonly contents: readonly ChestItem[];
  readonly openedAt: string | null;
}

/** What a player holds from chests, beside their balance. */
export interface Inventory {
  readonly boostGames: number;
  readonly boostMultiplier: number;
  readonly frame: string | null;
  readonly title: string | null;
  /** Free entries held, by fee rung. */
  readonly tickets: Record<string, number>;
  readonly owned: readonly string[];
}

export function chests(): Promise<Result<{ chests: ChestView[] }>> {
  return call<{ chests: ChestView[] }>('/api/chests', WITH_SESSION);
}

/**
 * Open one. The server applies the stored contents exactly once; a retry
 * returns the same roll with `applied: false`, so a lost response cannot cost
 * a player their chest or pay them twice.
 */
export function openChest(
  id: string,
): Promise<Result<{ chest: ChestView; applied: boolean; held?: boolean; inventory: Inventory }>> {
  return call<{ chest: ChestView; applied: boolean; held?: boolean; inventory: Inventory }>(
    `/api/chests/${encodeURIComponent(id)}/open`,
    { ...WITH_SESSION, method: 'POST', body: '{}' },
  );
}

/* ------------------------------------------------------------------ *
 * Leaderboard (Blockari's, server-ranked; see server/src/leaderboard.ts)
 * ------------------------------------------------------------------ */

/**
 * The server's board ids (Blockari's): `combo` is the CARD STREAK board (Blockari's Combos; the on-screen name is "Card
 * streaks", see `ui/leaderboard-data.ts`) and `streak` the WIN STREAK board.
 */
export type LeaderboardBoard = 'xp' | 'won' | 'score' | 'combo' | 'streak';
export type LeaderboardPeriod = 'week' | 'all';

/** One ranked player: the public badge and the board's number, nothing else. */
export interface LeaderboardRow {
  /** 1-based and unique: ties are broken by who got there first. */
  readonly position: number;
  readonly username: string;
  readonly xpLevel: number;
  readonly rank: RankView;
  /** XP, net $CHAIN (may be negative), a score, a card-streak multiplier (5 = 5X) or a number of wins in a row. */
  readonly value: number;
  /** A guest account: on the board, tagged, never paid a weekly chest. */
  readonly guest: boolean;
}

/** The caller's own row; `position` and `value` are null when they are not on this board. */
export interface LeaderboardMe {
  readonly position: number | null;
  readonly username: string;
  readonly xpLevel: number;
  readonly rank: RankView;
  readonly value: number | null;
  readonly guest: boolean;
}

export interface LeaderboardView {
  readonly board: LeaderboardBoard;
  readonly period: LeaderboardPeriod;
  readonly total: number;
  readonly rows: readonly LeaderboardRow[];
  /** Null signed out or as a guest. */
  readonly me: LeaderboardMe | null;
  /** The weekly reset (next Monday 00:00 UTC); null for all time. */
  readonly resetsAt: string | null;
  readonly generatedAt: string;
}

export function leaderboard(
  board: LeaderboardBoard,
  period: LeaderboardPeriod,
  limit = 100,
): Promise<Result<LeaderboardView>> {
  return call<LeaderboardView>(`/api/leaderboard?board=${board}&period=${period}&limit=${limit}`, WITH_SESSION);
}

/** The published odds. Public, and deliberately so. */
export function chestOdds(): Promise<Result<ChestOdds>> {
  return call<ChestOdds>('/api/chests/odds');
}

export function inventory(): Promise<Result<Inventory>> {
  return call<Inventory>('/api/inventory', WITH_SESSION);
}

export function equipCosmetic(kind: 'frame' | 'title', itemId: string | null): Promise<Result<Inventory>> {
  return call<Inventory>('/api/inventory/equip', {
    ...WITH_SESSION,
    method: 'POST',
    body: JSON.stringify({ kind, itemId }),
  });
}

/** One opened chest in the rewards history. */
export interface RewardEntry {
  readonly chestId: string;
  readonly source: string;
  readonly sourceRef: string;
  readonly rarity: string;
  readonly openedAt: string;
  readonly contents: ChestItem[];
}

/** The profile's Rewards tab (`/api/me/rewards`), private to the session user. */
export interface Rewards {
  readonly chestsEarned: number;
  readonly chestsOpened: number;
  readonly chainFromChests: number;
  readonly tickets: ReadonlyArray<{ fee: number; count: number }>;
  readonly ticketCount: number;
  readonly frames: ReadonlyArray<{ id: string; acquiredAt: string }>;
  readonly titles: ReadonlyArray<{ id: string; acquiredAt: string }>;
  readonly bonuses: ReadonlyArray<{ kind: 'xp-boost'; multiplier: number; usesLeft: number; expires: string | null }>;
  readonly history: RewardEntry[];
  /** Cursor for the next (older) page; null on the last. */
  readonly next: string | null;
}

export async function myRewards(cursor?: string): Promise<Result<Rewards>> {
  const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  const response = await call<{ rewards: Rewards }>(`/api/me/rewards${q}`, WITH_SESSION);
  if (!response.ok) return response;
  return { ok: true, value: response.value.rewards };
}

/** A public profile. No session needed to read one. */
export interface Profile {
  readonly username: string;
  readonly isGuest: boolean;
  /**
   * Current $CHAIN, public like the rest of the chain. For a visitor it is the player's holdings (spendable plus
   * the stakes in challenges not yet settled), so a new challenge never moves it; the player themselves get
   * their spendable balance. Absent on an older server.
   */
  readonly balance?: number;
  readonly createdAt: string;
  /**
   * When the player was last active, floored to the start of the hour by the server (never the exact time); null when
   * unknown, absent on an older server.
   */
  readonly lastSeenAt?: string | null;
  readonly xp: number;
  readonly xpLevel: number;
  readonly rank: RankView;
  readonly prevThreshold: number;
  readonly nextThreshold: number;
  readonly gamesPlayed: number;
  readonly challengesWon: number;
  readonly challengesPlayed: number;
  /** Mean final score of the played 3:00 games, a whole number; null until there is one (absent on an older server). */
  readonly avgScore?: number | null;
  readonly bestScore: number;
  /** The highest level of a game shown on this profile; null with none (an older server sent 1). */
  readonly bestLevel: number | null;
  /** The longest run of consecutive pile clears in any round (0 on an older server). */
  readonly bestStreak?: number;
  /** The equipped chest frame and title, public (absent on an older server). */
  readonly frame?: string | null;
  readonly title?: string | null;
  readonly chainStaked: number;
  readonly chainWon: number;
  readonly chainLost: number;
  readonly chainPnl: number;
  /**
   * Won over staked, as a percentage — a stake-weighted win rate, never
   * negative. Null until something has actually been at risk.
   */
  readonly chainPnlPct: number | null;
  /** Decided with no winner (a tie). Absent on an older server. */
  readonly challengesDrawn?: number;
}

/** One of a player's best rounds. */
export interface BestScore {
  readonly score: number;
  readonly level: number | null;
  readonly at: string;
  /** The entry fee of the challenge it was played in; 0 for a free one. */
  readonly fee: number;
}

/** The player's own money, from the session only (`/api/me/finance`). Same shape and definitions as Blockari's. */
export interface Finance {
  /** Won minus lost on settled matches; refunds and chest $CHAIN are not in it. */
  readonly netChain: number;
  readonly balance: number;
  /** Stake on challenges not yet decided. */
  readonly inChallenges: { readonly chain: number; readonly count: number };
  readonly wins: number;
  readonly losses: number;
  readonly winPct: number | null;
  readonly won: number;
  readonly lost: number;
  readonly staked: number;
  readonly roiPct: number | null;
  readonly returned: { readonly chain: number; readonly count: number };
  readonly chestChain: number;
}

export async function financeOf(): Promise<Result<Finance>> {
  const response = await call<{ finance: Finance }>('/api/me/finance', WITH_SESSION);
  if (!response.ok) return response;
  return { ok: true, value: response.value.finance };
}

/** How many challenges the player has made, how many still wait for a taker, and how many they took. */
export interface ChallengeCounts {
  readonly created: number;
  readonly open: number;
  readonly taken: number;
  /** The player's own open challenges (private too) by wager: "N yours" under a Create chip, never in Take's counts. */
  readonly openByFee?: Readonly<Record<string, number>>;
}

export async function myChallengeCounts(): Promise<Result<ChallengeCounts>> {
  const response = await call<{ counts: ChallengeCounts }>('/api/me/challenge-counts', WITH_SESSION);
  if (!response.ok) return response;
  return { ok: true, value: response.value.counts };
}

/** The signed-in player's top 25 rounds (`/api/me/scores`). */
export async function bestScores(): Promise<Result<BestScore[]>> {
  const response = await call<{ scores: BestScore[] }>('/api/me/scores', WITH_SESSION);
  if (!response.ok) return response;
  return { ok: true, value: response.value.scores ?? [] };
}

export async function profileOf(username: string): Promise<Result<Profile>> {
  const response = await call<{ profile: Profile }>(
    `/api/users/${encodeURIComponent(username)}`,
  );
  if (!response.ok) return response;
  if (!response.value.profile) return { ok: false, error: 'malformed response' };
  return { ok: true, value: response.value.profile };
}

/**
 * One row of `GET /api/users/:username/games`: a SETTLED game. A visitor never gets an open challenge here (the
 * server leaves it out, no placeholder), and no id, code or seed is ever in a row.
 */
export interface GameRow {
  /** `lobby`: a legacy free game with no creator. */
  readonly mode: 'challenge' | 'lobby';
  readonly at: string;
  readonly wager: number;
  readonly result: 'win' | 'loss' | 'expired' | 'voided' | 'open' | 'playing';
  readonly role: 'creator' | 'taker' | null;
  /** The player's own score; null when never played (a timed-out seat) or unfinished. */
  readonly score: number | null;
  /** What the challenge did to the player's $CHAIN: payout - stake for a win, -stake for a loss, else 0. */
  readonly net: number;
  /** A void or expiry paid the stake back in $CHAIN. */
  readonly refunded: boolean;
  /** The other side of a settled challenge; `score` null when they never played it (a forfeit). */
  readonly opponent: { readonly username: string; readonly level: number; readonly rank: RankView; readonly score: number | null } | null;
}

export interface GamesPage {
  readonly games: GameRow[];
  /** Pass to `userGames` for the next page; null on the last. */
  readonly next: string | null;
}

/** A player's game history, newest first, 20 a page; `cursor` is the previous page's `next`. Public. */
export async function userGames(username: string, cursor?: string | null): Promise<Result<GamesPage>> {
  const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  const response = await call<GamesPage>(`/api/users/${encodeURIComponent(username)}/games${q}`);
  if (!response.ok) return response;
  if (!Array.isArray(response.value.games)) return { ok: false, error: 'malformed response' };
  return response;
}

/** One row of the streaks sheet: an exact streak length, how many times it was hit, and the latest. */
export interface StreakCount {
  /** 5 = "5X" (a card streak) or "5 wins in a row" (a win streak). */
  readonly length: number;
  readonly count: number;
  /** When the latest one ended: the round finished (a card streak) or the last win settled (a win streak). */
  readonly last: string;
}

/**
 * `GET /api/users/:username/streaks`: how many times a player hit each streak length. Public, settled results only, no
 * id in any row. `combos` are the in-game streak multipliers the Best streak tile reads (the sheet's "Card streaks"),
 * one count per round at its best; `winRuns` are consecutive decided challenge wins (the rule the streak chests use).
 */
export interface TopStreaks {
  /** 0 when the latest decided challenge was lost (or none was won). */
  readonly currentWinRun: number;
  /** One row per length, longest first; `ongoing`: the latest one of that length is the current streak (`started`: its first win). */
  readonly winRuns: readonly (StreakCount & { readonly ongoing: boolean; readonly started: string })[];
  readonly combos: readonly StreakCount[];
}

/** A player's streak counts, per length. Public. */
export async function userStreaks(username: string): Promise<Result<TopStreaks>> {
  const response = await call<{ streaks: TopStreaks }>(`/api/users/${encodeURIComponent(username)}/streaks`);
  if (!response.ok) return response;
  const s = response.value.streaks;
  if (!s || !Array.isArray(s.winRuns) || !Array.isArray(s.combos)) return { ok: false, error: 'malformed response' };
  return { ok: true, value: s };
}

export interface StakedDeal {
  readonly challengeId: string;
  readonly code: string | null;
  readonly entryFee: number;
  readonly pot: number;
  readonly payout: number;
  readonly balance: number;
  readonly expiresAt: string | null;
}

export interface ChallengeCard {
  readonly id: string;
  readonly code: string | null;
  readonly entryFee: number;
  readonly role: 'creator' | 'taker';
  readonly createdAt: string;
  /**
   * When the challenge last MOVED — max(created, resolved, either result).
   *
   * What the list sorts on and what a row's time label shows. `createdAt` is
   * when it was opened, which is not the same question and was the wrong
   * answer on every challenge that had since been played.
   */
  readonly activityAt: string;
  /**
   * Both seats bought — true while an opponent is playing but has not
   * finished. Distinct from `opponent`, which comes from their RESULT and so
   * stays null for exactly as long as the match is interesting.
   */
  readonly taken: boolean;
  /** Code-only, never in the queue. Wears a chip on the card. */
  readonly isPrivate: boolean;
  /** The deadline passed with the second seat still empty. */
  readonly expired: boolean;
  readonly resolvedAt: string | null;
  /** Voided (a round was lost with a server restart): both stakes refunded, no winner. History: "Voided — refunded". */
  readonly voided?: 'session-lost' | 'both-unplayed' | 'unpaid-seat' | null;
  /** This player's round was forfeited unplayed (timed out): a loss, not a score. History: "Lost · timed out". */
  readonly myForfeited?: boolean;
  /** The other side's round was forfeited unplayed: they never turned up, so the win is by forfeit. */
  readonly theirForfeited?: boolean;
  readonly myScore: number | null;
  readonly myLevel: number | null;
  /**
   * What it did to this player's $CHAIN, from their ledger (a ticket-funded loss is 0, a ticket win the whole payout);
   * 0 until it is decided. Absent from an older server: the row then reckons payout - stake.
   */
  readonly net?: number;
  /** Who took it. Null while it is still waiting. */
  readonly opponent: PlayerView | null;
  readonly theirScore: number | null;
  /** They have played; their score is withheld until you have one (an older server omits it). */
  readonly theirPlayed?: boolean;
  readonly theirLevel: number | null;
  /** Who scored higher, once both have played. Independent of any payout. */
  readonly outcome: 'won' | 'lost' | 'draw' | null;
  readonly won: boolean | null;
  readonly payout: number | null;
}

/** One challenge in full: the waiting room, and the scoreboard afterwards. */
export interface ChallengeView {
  readonly id: string;
  readonly code: string | null;
  readonly entryFee: number;
  readonly isPrivate: boolean;
  readonly role: 'creator' | 'taker';
  readonly createdAt: string;
  readonly expiresAt: string | null;
  readonly resolvedAt: string | null;
  /** How many players have been dealt this deck: 1 while it waits, 2 once taken. */
  readonly seats: number;
  readonly me: {
    score: number;
    level: number;
    elapsedMs: number | null;
    finished: boolean;
    xpLevel: number;
    rank: RankView;
    /** Your own round's card facts (the share card's stat tiles); older servers do not send them. */
    clears?: number;
    bestStreak?: number;
    reason?: 'deckEmpty' | 'bustedOut' | 'timeout';
  } | null;
  readonly opponent: {
    name: string;
    score: number | null;
    level: number | null;
    elapsedMs: number | null;
    finished: boolean;
    /** Never withheld — a rank is public, unlike the score on this deck. */
    xpLevel: number;
    rank: RankView;
  } | null;
  readonly result: { won: boolean; payout: number } | null;
  /** Who scored higher, once both have played. Independent of any payout. */
  readonly outcome: 'won' | 'lost' | 'draw' | null;
  /** Voided (a round was lost with a server restart): both stakes refunded, no winner. */
  readonly voided?: 'session-lost' | 'both-unplayed' | 'unpaid-seat' | null;
  /** Only when voided: who sat in each seat, result or not, so every seat has a face. */
  readonly voidedSeats?: {
    me: { xpLevel: number; rank: RankView };
    opponent: { name: string; xpLevel: number; rank: RankView } | null;
  } | null;
  /** Your round was forfeited unplayed (timed out). */
  readonly myForfeited?: boolean;
  /** The opponent's round was forfeited unplayed: you won because they never turned up. */
  readonly opponentForfeited?: boolean;
}

/**
 * One challenge in full.
 *
 * The route answers `{ challenge: {...} }`, so this unwraps rather than
 * declaring the envelope away — a type that claims the fields are at the top
 * level compiles perfectly and hands the UI an object where every property is
 * `undefined`, which is the failure that does not announce itself.
 */
export async function challengeView(id: string): Promise<Result<ChallengeView>> {
  const response = await call<{ challenge: ChallengeView }>(
    `/api/challenges/${encodeURIComponent(id)}/view`,
    WITH_SESSION,
  );
  if (!response.ok) return response;
  if (!response.value.challenge) return { ok: false, error: 'malformed response' };
  return { ok: true, value: response.value.challenge };
}

/**
 * Every account call sends the session cookie.
 *
 * `credentials: 'include'` rather than the default, because the game and the
 * API are different origins during development and a cross-origin request
 * omits cookies unless asked. In production they share an origin and this
 * costs nothing.
 */
const WITH_SESSION: RequestInit = { credentials: 'include' };

/**
 * Who is signed in — and the daily top-up, if this call is what triggered it.
 *
 * `dailyGranted` is non-zero at most once a day, on whichever session check
 * happens to be first. The balance in `user` already includes it; the number is
 * returned separately only so the UI can say what just happened, because a
 * balance that silently grew by 100 looks like a bug to the person holding it.
 */
export function whoAmI(): Promise<Result<{ user: Account | null; dailyGranted: number }>> {
  return call<{ user: Account | null; dailyGranted: number }>('/api/auth/me', WITH_SESSION);
}

/**
 * Sign in as a guest, claiming the id this browser already plays under.
 *
 * That id has been in local storage since before accounts existed and every
 * result this browser ever posted is keyed by it, so claiming it is what lets a
 * returning player keep their history rather than arriving as somebody new on
 * their own machine.
 */
export function playAsGuest(legacyPlayerId: string | null): Promise<Result<{ user: Account }>> {
  return call<{ user: Account }>('/api/auth/guest', {
    ...WITH_SESSION,
    method: 'POST',
    // Only a pre-accounts browser id, once, to reclaim its history — the server ignores any other.
    body: JSON.stringify(legacyPlayerId ? { playerId: legacyPlayerId } : {}),
  });
}

/** POST /api/auth/register: the account, and what registering made claimable. */
export interface Registration {
  readonly user: Account;
  /** A guest was upgraded in place (same id, XP, results, chests). */
  readonly converted: boolean;
  /** The guest's held / unopened chests, now ordinary unopened chests. */
  readonly released: readonly ChestSummary[];
  /** The one welcome chest; null on a retry. */
  readonly welcome: ChestSummary | null;
}

/**
 * Sign up. A guest's session is upgraded IN PLACE, so the id, XP, games and
 * chests follow; a retry whose answer was lost returns success and awards
 * nothing twice.
 */
export function register(username: string, password: string): Promise<Result<Registration>> {
  return call<Registration>('/api/auth/register', {
    ...WITH_SESSION,
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
}

/** A friendly name to offer at sign-up ("SharpAce17"), free a moment ago. */
export function suggestName(): Promise<Result<{ name: string }>> {
  return call<{ name: string }>('/api/auth/name-suggestion', WITH_SESSION);
}

/** What a guest would leave behind by signing in to an account (409 `guest-has-value`, `GuestValue` in the body). */
export interface GuestValue {
  /** The guest's $CHAIN. */
  readonly balance: number;
  /** Staked challenges it has a seat on that are not resolved yet: the stake is still in play. */
  readonly seats: number;
  readonly tickets: number;
  /** Unopened chests (a guest's held ones included). */
  readonly chests: number;
}

/** The guest's holdings out of a refused login, or null when the answer was not that refusal. */
export function guestValueOf(result: { ok: boolean; error?: string; status?: number; body?: unknown }): GuestValue | null {
  if (result.ok || result.error !== 'guest-has-value') return null;
  const value = (result.body as { value?: Partial<GuestValue> } | null | undefined)?.value;
  if (!value || typeof value.balance !== 'number') return null;
  return { balance: value.balance, seats: value.seats ?? 0, tickets: value.tickets ?? 0, chests: value.chests ?? 0 };
}

/**
 * Sign in. A guest session this browser holds is left behind for good (a guest cannot be signed back in to): when it
 * holds anything the server answers 409 `guest-has-value` instead, and signs in only when asked again with
 * `abandonGuest`.
 */
export function login(
  username: string,
  password: string,
  abandonGuest = false,
): Promise<Result<{ user: Account }>> {
  return call<{ user: Account }>('/api/auth/login', {
    ...WITH_SESSION,
    method: 'POST',
    body: JSON.stringify({ username, password, ...(abandonGuest ? { abandonGuest: true } : {}) }),
  });
}

/**
 * Sign out. A GUEST that holds value is not signed out: the server answers 409 `guest-has-value` (`guestValueOf`)
 * and signs out only when asked again with `abandonGuest`, because nobody can sign back in to a guest.
 */
export function logout(abandonGuest = false): Promise<Result<{ ok: true }>> {
  return call<{ ok: true }>('/api/auth/logout', {
    ...WITH_SESSION,
    method: 'POST',
    ...(abandonGuest ? { body: JSON.stringify({ abandonGuest: true }) } : {}),
  });
}

/** The quick-pick chips and the platform's bounds for a custom wager. An older server answers `fees` alone. */
export function entryFees(): Promise<Result<{ fees: number[]; min?: number; max?: number }>> {
  return call<{ fees: number[]; min?: number; max?: number }>('/api/challenges/fees');
}

/** A player as they appear next to their name anywhere in the UI. */
export interface PlayerView {
  readonly name: string;
  readonly xpLevel: number;
  readonly rank: RankView;
}

/** One stake: how many are open. Never who set them — a creator is anonymous until somebody takes the challenge. */
export interface QueueTier {
  readonly open: number;
}

/** What waits at each tier. Cached server-side. */
/** One row of the open queue: a challenge a take could hand out right now. */
export interface OpenChallenge {
  readonly id: string;
  readonly code: string | null;
  readonly entryFee: number;
  readonly createdAt: string;
}

/** The open queue: the stakes nearest the one asked for first, oldest first among equals. Sends the session so the viewer's own are left out. */
export function openQueue(fee?: number, custom = false): Promise<Result<{ open: OpenChallenge[] }>> {
  // `custom`: only the amounts that are not a quick pick, smallest first (the Take screen's CUSTOM tile).
  const q = custom ? '?custom=1' : fee !== undefined ? `?fee=${encodeURIComponent(String(fee))}` : '';
  return call<{ open: OpenChallenge[] }>(`/api/challenges/open${q}`, WITH_SESSION);
}

/** What waits at each wager. Sends the session: the viewer's own challenges are left out of the counts. */
export function queueCounts(): Promise<Result<{ queues: Record<string, QueueTier> }>> {
  return call<{ queues: Record<string, QueueTier> }>('/api/challenges/queues', WITH_SESSION);
}

export function createChallenge(
  entryFee: number,
  isPrivate: boolean,
  /** Spend a free-entry ticket at this rung, if one is held. */
  useTicket = false,
): Promise<Result<StakedDeal>> {
  return call<StakedDeal>('/api/challenges', {
    ...WITH_SESSION,
    method: 'POST',
    body: JSON.stringify({ entryFee, isPrivate, useTicket }),
  });
}

export function takeChallenge(entryFee: number, useTicket = false): Promise<Result<StakedDeal>> {
  return call<StakedDeal>('/api/challenges/take', {
    ...WITH_SESSION,
    method: 'POST',
    body: JSON.stringify({ entryFee, useTicket }),
  });
}

export function takeChallengeByCode(code: string): Promise<Result<StakedDeal>> {
  return call<StakedDeal>(`/api/challenges/take/${encodeURIComponent(code)}`, {
    ...WITH_SESSION,
    method: 'POST',
  });
}

/** A player's whole record, counted server-side over every challenge. */
export interface ChallengeRecord {
  readonly played: number;
  readonly won: number;
  readonly drawn: number;
}

export function myChallenges(): Promise<
  Result<{ challenges: ChallengeCard[]; record: ChallengeRecord | null }>
> {
  return call<{ challenges: ChallengeCard[]; record: ChallengeRecord | null }>(
    '/api/challenges/mine',
    WITH_SESSION,
  );
}

/**
 * The player's OWN challenges that are still going (waiting for a taker, or being played), for Create's "N open"
 * sheet: `GET /api/challenges/mine?open=1`. Nothing of anyone else's.
 */
export async function myOpenChallenges(): Promise<Result<ChallengeCard[]>> {
  const response = await call<{ challenges: ChallengeCard[] }>('/api/challenges/mine?open=1', WITH_SESSION);
  if (!response.ok) return response;
  if (!Array.isArray(response.value.challenges)) return { ok: false, error: 'malformed response' };
  return { ok: true, value: response.value.challenges };
}

/** Begin a ranked round. The token this returns authorises every move in it. */
export function openSession(input: { challengeId: string }): Promise<Result<SessionOpened>> {
  const { challengeId } = input;
  return call<SessionOpened>(`/api/challenges/${encodeURIComponent(challengeId)}/sessions`, {
    // The session is the identity: no player id goes with this request, and the server would not read one.
    ...WITH_SESSION,
    method: 'POST',
    // Whether this is a touch device, which the user-agent alone cannot say:
    // iPadOS has reported itself as "Macintosh" since 2019, and a Mac with a
    // touch screen does not exist. Without this every iPad is counted as a
    // desktop, on a game that is mostly played on tablets and phones.
    body: JSON.stringify({ touch: (globalThis.navigator?.maxTouchPoints ?? 0) > 1 }),
  });
}

/**
 * Store a finished challenge game's share cards (both sizes, JPEG, base64)
 * against the viewer's result, and get back the `/s/:id` link that unfurls
 * into the link card. One row per (challenge, player): a re-share overwrites.
 */
export function shareCard(
  challengeId: string,
  cards: { story: string; link: string },
  /** `challenge-me`: the brag card of a SETTLED challenge, whose link lands on the sharer's profile. */
  kind?: 'challenge-me',
): Promise<Result<{ id: string; url: string }>> {
  return call<{ id: string; url: string }>(`/api/share/${encodeURIComponent(challengeId)}`, {
    ...WITH_SESSION,
    method: 'POST',
    body: JSON.stringify(kind ? { ...cards, kind } : cards),
  });
}

/**
 * Hand the round in. `index` is the move's place in the round (the count of moves the server has applied), so
 * a resend after a lost reply is answered with the first answer instead of being applied twice.
 */
export function forfeitSession(
  session: { id: string; token: string },
  index?: number,
  driver?: string,
  /** The first call of a table that has not synced yet: it takes the round over if another driver has it. */
  claim?: boolean,
): Promise<Result<SessionStep>> {
  return call<SessionStep>(`/api/sessions/${encodeURIComponent(session.id)}/forfeit`, {
    // The token alone is not enough: the server also wants the cookie to name the round's own player.
    ...WITH_SESSION,
    method: 'POST',
    headers: { authorization: `Bearer ${session.token}` },
    ...(index === undefined && driver === undefined
      ? {}
      : {
          body: JSON.stringify({
            ...(index === undefined ? {} : { index }),
            ...(driver === undefined ? {} : { driver }),
            ...(claim ? { claim: true } : {}),
          }),
        }),
  });
}

/** The table as the server has it, and the standings if this read is the one that settled the round. */
export function readSession(
  session: { id: string; token: string },
  timeoutMs?: number,
): Promise<Result<SessionStep & { seq?: number }>> {
  return call<SessionStep & { seq?: number }>(
    `/api/sessions/${encodeURIComponent(session.id)}`,
    { ...WITH_SESSION, headers: { authorization: `Bearer ${session.token}` } },
    timeoutMs,
  );
}

/**
 * The signed-in player's own live round on a challenge, to pick it up again after a refresh. Cookie
 * auth: the session id and token a reload lost come back with it, and so does `seq`, the index the next move
 * carries. 404 `no-live-session` when there is none (it ran out, or was recorded).
 */
export function liveSession(challengeId: string, timeoutMs?: number): Promise<Result<SessionOpened>> {
  return call<SessionOpened>(
    `/api/challenges/${encodeURIComponent(challengeId)}/session`,
    WITH_SESSION,
    timeoutMs,
  );
}

/**
 * One deal call over HTTP: `POST /api/games/:id/deal`, the twin of a socket `sync`, with the body the socket
 * message carries. Identity is the session cookie. The error is the protocol's code when the server gave
 * one (`game-over`, `move-gap`, ...), and anything else for a failure that never reached the protocol.
 */
export function postDeal(gameId: string, body: object, timeoutMs: number = DEAL_TIMEOUT_MS): Promise<Result<unknown>> {
  return call<unknown>(
    `/api/games/${encodeURIComponent(gameId)}/deal`,
    { ...WITH_SESSION, method: 'POST', body: JSON.stringify(body) },
    timeoutMs,
  );
}


/* ------------------------------------------------------------ notifications -- */

/** The things a player is told about. Mirrors the server's closed set. */
export type NotificationKind =
  | 'challenge_taken'
  | 'challenge_won'
  | 'challenge_lost'
  | 'challenge_expired'
  | 'challenge_voided'
  | 'chest_earned';

export interface AppNotification {
  readonly id: string;
  readonly kind: NotificationKind;
  /** Null for a chest: it was earned by playing, not on one challenge. */
  readonly challengeId: string | null;
  readonly role: 'creator' | 'taker' | null;
  /** The chest this notice is about, when it is one. */
  readonly chestId?: string | null;
  /** On `chest_earned`: what it was for ("2nd place on the weekly $CHAIN board") and its starting rarity; absent or null on an older notice. */
  readonly chestLabel?: string | null;
  readonly chestRarity?: Rarity | null;
  readonly opponent: string | null;
  /** Signed, and always this player's own delta. Never the pot. */
  readonly amount: number;
  readonly myScore: number | null;
  readonly theirScore: number | null;
  readonly createdAt: string;
  readonly readAt: string | null;
}

export function notifications(
  after?: string | null,
): Promise<Result<{ items: AppNotification[]; unread: number }>> {
  const query = after ? `?after=${encodeURIComponent(after)}` : '';
  return call<{ items: AppNotification[]; unread: number }>(
    `/api/notifications${query}`,
    WITH_SESSION,
  );
}

/** Omit `ids` to mark everything read. An empty array marks nothing. */
export function markNotificationsRead(ids?: readonly string[]): Promise<Result<{ unread: number }>> {
  return call<{ unread: number }>('/api/notifications/read', {
    ...WITH_SESSION,
    method: 'POST',
    body: JSON.stringify(ids === undefined ? {} : { ids }),
  });
}

/* --------------------------------------------------------------- web push -- */

/** Null when push is not configured on this deployment — a 404, not an error. */
export async function vapidKey(): Promise<string | null> {
  const response = await call<{ publicKey: string }>('/api/push/vapid', WITH_SESSION);
  return response.ok ? response.value.publicKey : null;
}

export function subscribePush(subscription: PushSubscriptionJSON): Promise<Result<{ ok: true }>> {
  return call<{ ok: true }>('/api/push/subscribe', {
    ...WITH_SESSION,
    method: 'POST',
    body: JSON.stringify({ subscription }),
  });
}

export function unsubscribePush(endpoint: string): Promise<Result<{ ok: true }>> {
  return call<{ ok: true }>('/api/push/subscribe', {
    ...WITH_SESSION,
    method: 'DELETE',
    body: JSON.stringify({ endpoint }),
  });
}
